import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Eye, LockKeyhole, ShieldCheck } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { api } from "../services/api";

const fieldClass =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#3991D5] focus:ring-4 focus:ring-[#70C6FE]/20";

const primaryButtonClass =
  "inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#3991D5] px-5 py-3.5 text-sm font-bold text-white transition hover:bg-[#1a6db5] focus:outline-none focus:ring-4 focus:ring-[#70C6FE]/30";

function ResetPassword() {
  const navigate = useNavigate();
  const { token } = useParams();
  const { setUser } = useAuth();
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    const formData = new FormData(event.currentTarget);
    const password = String(formData.get("password") || "");
    const confirmPassword = String(formData.get("confirmPassword") || "");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setSaving(true);
      const data = await api.patch(`/auth/reset-password/${token}`, { password });
      localStorage.setItem("authToken", data.token);
      localStorage.setItem("authUser", JSON.stringify(data.user));
      setUser(data.user);
      setMessage("Password updated. Redirecting to your account...");
      setTimeout(() => {
        navigate(data.user?.role === "admin" ? "/dashboard" : "/my-account", { replace: true });
      }, 800);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-[#f7fbff] px-4 py-12 text-slate-900 sm:px-6 lg:py-16">
      <div className="container">
        <div className="mx-auto max-w-xl rounded-[28px] border border-[#3991D5]/10 bg-white p-6 shadow-[0_18px_60px_rgba(57,145,213,0.10)] sm:p-8">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#3991D5] text-white">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="mt-6 font-gopher text-4xl font-black uppercase text-slate-950">
            Reset password
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Create a new password for your Bobbin USA account. Reset links expire after 15 minutes and can only be used once.
          </p>

          {(error || message) && (
            <div
              className={`mt-5 rounded-xl px-4 py-3 text-sm font-semibold ${
                error
                  ? "border border-red-200 bg-red-50 text-red-700"
                  : "border border-emerald-200 bg-emerald-50 text-emerald-800"
              }`}
            >
              <div className="flex items-start gap-2">
                {!error && <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />}
                <span>{error || message}</span>
              </div>
            </div>
          )}

          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-700">New password</span>
              <span className="relative block">
                <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input className={`${fieldClass} pl-11 pr-11`} name="password" placeholder="At least 8 characters" type="password" required />
                <Eye className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              </span>
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-700">Confirm password</span>
              <span className="relative block">
                <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input className={`${fieldClass} pl-11`} name="confirmPassword" placeholder="Re-enter new password" type="password" required />
              </span>
            </label>

            <button className={primaryButtonClass} disabled={saving} type="submit">
              {saving ? "Updating password..." : "Update password"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-center text-sm font-semibold text-slate-600">
            Remember your password?{" "}
            <Link className="font-bold text-[#1278b5]" to="/account">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ResetPassword;
